import { formatPrice, formatPriceWhole, type MoneyFormat } from '@/lib/c2c/format'
import { longDate, monthName } from '@/i18n/calendar'
import type { C2CResponse, PriceHistoryPoint } from '@/services/wapi/types/c2c'

/**
 * Structured price chart data for rendering 3 charts.
 */
export interface PriceChartData {
    daily: PriceHistoryPoint[] | null
    monthly: PriceHistoryPoint[] | null
    daysUntilDepart: PriceHistoryPoint[] | null
    xTickValuesDaily: (string | number)[]
    xTickValuesMonthly: number[]
    xTickValuesDepartDays: number[]
    interpolationValues: Record<string, string>
}

/** A whole-unit fare for the price tokens ("$22"). WAPI rounds these, so nexus does too —
 *  the c2c response carries them raw (21.98) and the two must read alike. */
function wholeFare(value: number | undefined, money: MoneyFormat): string | null {
    return value == null ? null : formatPriceWhole(value, money)
}

/**
 * Replaces __variable__ placeholders in a template string with values.
 * Unknown placeholders are left as-is.
 */
export function interpolateContent(template: string, values: Record<string, string>): string {
    return template.replace(/__([a-zA-Z0-9_]+)__/g, (match, key: string) => {
        return values[key] ?? match
    })
}

/** Deliberately non-global: a `/g` regex carries `lastIndex` between `.test()` calls. */
const UNFILLED_TOKEN = /__[a-zA-Z0-9_]+__/

/**
 * Interpolated copy, or `null` when any token could not be filled.
 *
 * `interpolateContent` leaves an unknown token in place, which is right mid-pipeline —
 * a later pass owns its own tokens. At the point copy is RENDERED there is no later
 * pass, so a surviving token is a sentence the data could not complete, and shipping it
 * puts `__lowUpcomingWeekPrice__` in front of a reader.
 *
 * Presence of *some* price data does not imply presence of *every* price token: a route
 * can have monthly history and no upcoming-week fare. So the check is per string, not
 * per section — the unfillable sentence drops and its fillable siblings still render.
 */
export function interpolateIfComplete(
    template: string,
    values: Record<string, string>,
): string | null {
    const filled = interpolateContent(template, values)
    return UNFILLED_TOKEN.test(filled) ? null : filled
}

/**
 * The price-history/stats interpolation vocabulary — the `__token__` values shared by the
 * price-history chart (`PriceHistoryChart`) and the FAQ / long-content copy (`selectFAQItems`).
 * Both surfaces render the same store templates (e.g. `cheapestTicketsDescription`), so a
 * chart token such as `__savingsPrice__` or `__lowPriceMonthPrice__` that also appears in a
 * FAQ answer must fill identically in both places rather than leak raw in the FAQ.
 *
 * Sources both `priceHistory.metrics` (month picks + upcoming prices) and `stats.metrics`
 * (savingsPrice, chart average price). `price` here is the chart AVERAGE
 * (`cheapestAveragePriceUsd`) — the value the "cost __price__ on average" copy means. The
 * FAQ path applies these values FIRST so `__price__` resolves to this average in both the
 * visible price-history section and the FAQ (interpolateVariables' min-fare `__price__`
 * remains only as a fallback when the average is absent).
 */
export function buildPriceHistoryInterpolationValues(
    data: C2CResponse,
    money: MoneyFormat,
): Record<string, string> {
    const interpolationValues: Record<string, string> = {}
    const { locale } = money

    const metrics = data.priceHistory?.metrics
    if (metrics) {
        const entries: [string, string | null][] = [
            ['lowPriceMonthPrice', wholeFare(metrics.lowPriceMonthPrice, money)],
            ['highPriceMonthPrice', wholeFare(metrics.highPriceMonthPrice, money)],
            ['lowPriceMonth', monthName(metrics.lowPriceMonth, locale)],
            ['highPriceMonth', monthName(metrics.highPriceMonth, locale)],
            ['lowUpcomingWeekPrice', wholeFare(metrics.lowUpcomingWeekPrice, money)],
            ['highUpcomingWeekPrice', wholeFare(metrics.highUpcomingWeekPrice, money)],
            ['lowUpcomingMonthPrice', wholeFare(metrics.lowUpcomingMonthPrice, money)],
            ['highUpcomingMonthPrice', wholeFare(metrics.highUpcomingMonthPrice, money)],
            ['lowUpcomingMonthPriceDate', longDate(metrics.lowUpcomingMonthPriceDate, locale)],
            ['lowUpcomingWeekDate', longDate(metrics.lowUpcomingWeekDate, locale)],
            ['highUpcomingWeekDate', longDate(metrics.highUpcomingWeekDate, locale)],
            ['commonMonth', monthName(metrics.commonMonth, locale)],
            ['commonWeekPrice', wholeFare(metrics.commonWeekPrice, money)],
            ['commonMonthPrice', wholeFare(metrics.commonMonthPrice, money)],
        ]

        for (const [key, value] of entries) {
            if (value) {
                interpolationValues[key] = value
            }
        }
    }

    // From stats.metrics (cheapestAveragePriceUsd → __price__, savingsPriceUsd → __savingsPrice__)
    //
    // Truthiness, not `!= null`: a ZERO is not a value worth rendering here. The copy
    // these fill makes a claim — "you can save up to __savingsPrice__ by booking
    // __bestPriceDaysOut__ days ahead" — and at zero that claim is false advice, telling
    // a reader to book weeks early to save nothing. ui-react gates its whole pricing
    // block on `!!cheapestPrice && !!savingsPriceUsd` for the same reason
    // (PriceChart.jsx), so this matches the behavior the legacy stack already has.
    //
    // Omitting the key rather than special-casing the sentence is what makes it reach
    // every surface: the token then goes unfilled, and the completeness guard drops the
    // copy wherever it appears — the chart section and the FAQ answer alike.
    const statsMetrics = data.stats?.metrics
    if (statsMetrics?.cheapestAveragePriceUsd) {
        interpolationValues.price = formatPrice(statsMetrics.cheapestAveragePriceUsd, money)
    }
    if (statsMetrics?.savingsPriceUsd) {
        interpolationValues.savingsPrice = formatPrice(statsMetrics.savingsPriceUsd, money)
    }

    return interpolationValues
}

/**
 * Extracts and transforms price history data from WAPI response into 3-chart format.
 * Returns null when no price data is available.
 */
export function selectPriceHistory(data: C2CResponse, money: MoneyFormat): PriceChartData | null {
    const raw = data.priceHistory
    if (!raw || typeof raw !== 'object') {
        return null
    }

    const { daily, monthly, daysUntilDepart } = raw

    const hasDaily = daily && daily.length > 0
    const hasMonthly = monthly && monthly.length > 0
    const hasDaysUntilDepart = daysUntilDepart && daysUntilDepart.length > 0

    if (!hasDaily && !hasMonthly && !hasDaysUntilDepart) {
        return null
    }

    // Compute xTickValues for each chart (matching ui-react mapPriceHistory)
    const xTickValuesDaily = hasDaily
        ? daily.reduce<(string | number)[]>((arr, { x }, index) => {
              if (index === 0 || index === Math.ceil(daily.length / 2)) {
                  return [...arr, x]
              }
              return arr
          }, [])
        : []

    const xTickValuesMonthly = hasMonthly ? monthly.map((point) => point.x as number) : []

    const xTickValuesDepartDays = hasDaysUntilDepart
        ? daysUntilDepart.reduce<number[]>((arr, { x }) => {
              if (typeof x === 'number' && x % 10 === 0) {
                  return [...arr, x]
              }
              return arr
          }, [])
        : []

    const interpolationValues = buildPriceHistoryInterpolationValues(data, money)

    return {
        daily: hasDaily ? daily : null,
        monthly: hasMonthly ? monthly : null,
        daysUntilDepart: hasDaysUntilDepart ? daysUntilDepart : null,
        xTickValuesDaily,
        xTickValuesMonthly,
        xTickValuesDepartDays,
        interpolationValues,
    }
}
